import * as Sentry from "@sentry/browser";

const APP_VERSION_STORAGE_KEY = "onepaper-app-version";
const APP_VERSION_ENDPOINT = "/version.json";
const APP_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const BUILD_VERSION_PLACEHOLDER = "{version}";

type VersionRefreshDeps = {
  fetchImpl?: typeof fetch;
  storage?: Pick<Storage, "getItem" | "setItem">;
  getCurrentVersion?: () => string | null;
  versionEndpoint?: string;
  forceCheck?: boolean;
};

const readCurrentVersion = () => {
  const metaVersion = document
    .querySelector('meta[name="version"]')
    ?.getAttribute("content");

  if (metaVersion && metaVersion !== BUILD_VERSION_PLACEHOLDER) {
    return metaVersion;
  }

  return window.__EXCALIDRAW_SHA__ ?? null;
};

const readRemoteVersion = async (
  fetchImpl: typeof fetch,
  versionEndpoint: string,
) => {
  const response = await fetchImpl(versionEndpoint, { cache: "no-store" });
  if (!response.ok) {
    return null;
  }

  const payload = (await response.json()) as { version?: unknown };
  return typeof payload.version === "string" && payload.version.trim()
    ? payload.version.trim()
    : null;
};

const buildVersionEndpoint = (versionEndpoint: string) => {
  const url = new URL(versionEndpoint, window.location.origin);
  url.searchParams.set("ts", Date.now().toString(36));
  return url.toString();
};

export const ensureFreshAppVersion = async (deps: VersionRefreshDeps = {}) => {
  if (!import.meta.env.PROD && !deps.forceCheck) {
    return;
  }

  const fetchImpl = deps.fetchImpl ?? globalThis.fetch?.bind(globalThis);
  if (!fetchImpl) {
    return;
  }

  const storage = deps.storage ?? window.localStorage;
  const getCurrentVersion = deps.getCurrentVersion ?? readCurrentVersion;
  const versionEndpoint = deps.versionEndpoint ?? APP_VERSION_ENDPOINT;

  try {
    const currentVersion = getCurrentVersion();
    const remoteVersion = await readRemoteVersion(
      fetchImpl,
      buildVersionEndpoint(versionEndpoint),
    );

    if (!remoteVersion) {
      return;
    }

    const storedVersion = storage.getItem(APP_VERSION_STORAGE_KEY);
    if (storedVersion === remoteVersion) {
      return;
    }

    storage.setItem(APP_VERSION_STORAGE_KEY, remoteVersion);

    if (currentVersion === remoteVersion) {
      return;
    }

    console.info(
      `AppVersionWatch: Update available. Current: ${currentVersion}, Remote: ${remoteVersion}`,
    );
    Sentry.captureMessage(
      `AppVersionWatch update available: ${currentVersion} -> ${remoteVersion}`,
      { level: "info" },
    );
  } catch {
    // Best effort only. Failing the refresh check must never break app startup.
  }
};

export const startAppVersionWatch = (deps: VersionRefreshDeps = {}) => {
  if (!import.meta.env.PROD && !deps.forceCheck) {
    return () => undefined;
  }

  let refreshInFlight = false;

  const refresh = () => {
    if (refreshInFlight) {
      return;
    }

    refreshInFlight = true;
    void ensureFreshAppVersion({
      ...deps,
      forceCheck: true,
    }).finally(() => {
      refreshInFlight = false;
    });
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      refresh();
    }
  };

  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      refresh();
    }
  };

  window.addEventListener("focus", refresh);
  window.addEventListener("online", refresh);
  window.addEventListener("pageshow", onPageShow);
  document.addEventListener("visibilitychange", onVisibilityChange);

  const intervalId = window.setInterval(refresh, APP_VERSION_CHECK_INTERVAL_MS);

  return () => {
    window.removeEventListener("focus", refresh);
    window.removeEventListener("online", refresh);
    window.removeEventListener("pageshow", onPageShow);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearInterval(intervalId);
  };
};
